import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { IScheduleProfession } from '@interfaces/IScheduler';
import { TradePill } from '@components/ui/TradePill/TradePill';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { professionLabel } from './professionLabel';
import { useSiteProfessions } from '@hooks/useSiteProfessions';
import styles from './ProfessionPill.module.css';

interface IProfessionPillProps {
  profession: IScheduleProfession;
  /**
   * Append the contractor name in plain gray text after the pill (wide
   * surfaces — drawer Overview, picker rows). Omitted on tight columns (the
   * row-label Profession column) where it would overflow.
   */
  showContractor?: boolean;
  /**
   * Compact rendering for dense rows (the scheduler's ~28px label column):
   * the small TradePill variant with a matching smaller pictogram, sized to
   * truncate inside a fixed grid column instead of dominating the row.
   */
  compact?: boolean;
  /**
   * Opt-in for narrow table cells (the Productivity Activities tab): shrink the
   * row to its container and reveal the full rendered value — trade + specialty,
   * plus the contractor when `showContractor` — in a hover tooltip, mirroring the
   * admin Tags cell. Off by default, so every existing caller is unaffected.
   */
  truncate?: boolean;
}

/**
 * Profession pill for a task's profession snapshot — the shared render used across
 * the Scheduler (row-label Trade column, drawer Overview, Edit-tab picker) and
 * the Activities page (progress tracker, card, drawer). Label follows the admin
 * Tags-tab pattern (`cells.tsx`): `tradeLabel(t, tradeId) || tradeGroup`, then
 * `, specialty`; the contractor rides alongside as a gray suffix (like
 * `ProfessionChip`) so pills read identically wherever a profession is shown.
 */
export function ProfessionPill({
  profession,
  showContractor = false,
  compact = false,
  truncate = false,
}: IProfessionPillProps) {
  const { t } = useTranslation();
  // The stored snapshot's colour goes stale the moment an admin re-configures
  // the profession's per-project colour (the snapshot only refreshes on
  // re-pick, by design). The live site-professions catalog — already resolved
  // per project by the BFF and cached generously by TanStack Query — is
  // authoritative when it knows the profession; the snapshot colour remains
  // the fallback for catalog churn / loading (same self-disable-without-a-
  // router-siteId pattern as TradeIcon).
  const { siteId } = useParams<{ siteId: string }>();
  const professions = useSiteProfessions(siteId ?? '');
  const catalogEntry = professions.find((p) => p.id === profession.professionId);
  const backgroundColor = catalogEntry ? catalogEntry.backgroundColor : profession.backgroundColor;
  const label = professionLabel(t, profession);
  const withContractor = showContractor && profession.contractor;
  const rowClass = [styles.row, compact && styles.rowCompact, truncate && styles.rowTruncate]
    .filter(Boolean)
    .join(' ');
  const row = (
    <span className={rowClass}>
      <TradePill
        label={label}
        {...(backgroundColor ? { backgroundColor } : {})}
        {...(profession.tradeId ? { tradeId: profession.tradeId } : {})}
        {...(compact ? { size: 'sm' as const, iconSize: '1em' } : {})}
      />
      {withContractor ? <span className={styles.contractor}>{profession.contractor}</span> : null}
    </span>
  );
  if (!truncate) return row;
  // The full rendered value, for the hover reveal when the cell clips it.
  const fullLabel = withContractor ? `${label} - ${profession.contractor}` : label;
  return <Tooltip content={fullLabel}>{row}</Tooltip>;
}
